import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApplicationStatus = ({ partnerData, onRefresh, expanded = false }) => {
  const [showDetails, setShowDetails] = useState(expanded);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending_approval':
        return {
          label: 'Pending Review',
          color: 'warning',
          icon: 'Clock',
          description: 'Your application is being reviewed by our team'
        };
      case 'active':
        return {
          label: 'Active Partner',
          color: 'success',
          icon: 'CheckCircle',
          description: 'Your partnership is active and approved'
        };
      case 'inactive':
        return {
          label: 'Inactive',
          color: 'muted',
          icon: 'XCircle',
          description: 'Your partnership is currently inactive'
        };
      case 'suspended':
        return {
          label: 'Suspended',
          color: 'error',
          icon: 'AlertTriangle',
          description: 'Your partnership has been suspended'
        };
      default:
        return {
          label: 'Unknown',
          color: 'muted',
          icon: 'HelpCircle',
          description: 'Status information unavailable'
        };
    }
  };

  const statusInfo = getStatusInfo(partnerData?.status);

  const applicationStages = [
    {
      id: 1,
      title: 'Application Submitted',
      description: 'Initial application received',
      completed: true,
      date: partnerData?.created_at
    },
    {
      id: 2,
      title: 'Document Verification',
      description: 'Reviewing submitted documents',
      completed: partnerData?.status !== 'pending_approval',
      date: null
    },
    {
      id: 3,
      title: 'Compliance Check',
      description: 'Verifying compliance requirements',
      completed: partnerData?.compliance_verified,
      date: null
    },
    {
      id: 4,
      title: 'Final Approval',
      description: 'Final review and approval',
      completed: partnerData?.status === 'active',
      date: partnerData?.approved_at
    }
  ];

  const getColorClass = (color) => {
    switch (color) {
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'error': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  return (
    <div className="glass-container rounded-xl border border-border/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${getColorClass(statusInfo?.color)}`}>
            <Icon name={statusInfo?.icon} size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Application Status</h2>
            <p className="text-sm text-muted-foreground">{statusInfo?.description}</p>
          </div>
        </div>
        <div className={`px-4 py-2 rounded-lg border ${getColorClass(statusInfo?.color)}`}>
          <span className="font-semibold">{statusInfo?.label}</span>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 rounded-lg bg-accent/5 border border-border/30">
          <p className="text-2xl font-bold text-foreground">
            {applicationStages?.filter(s => s?.completed)?.length}/{applicationStages?.length}
          </p>
          <p className="text-sm text-muted-foreground">Stages Complete</p>
        </div>
        <div className="text-center p-4 rounded-lg bg-accent/5 border border-border/30">
          <p className="text-2xl font-bold text-foreground">
            {partnerData?.compliance_verified ? 'Yes' : 'Pending'}
          </p>
          <p className="text-sm text-muted-foreground">Compliance</p>
        </div>
        <div className="text-center p-4 rounded-lg bg-accent/5 border border-border/30">
          <p className="text-2xl font-bold text-foreground">
            {partnerData?.created_at ? new Date(partnerData.created_at)?.toLocaleDateString() : 'N/A'}
          </p>
          <p className="text-sm text-muted-foreground">Applied On</p>
        </div>
      </div>
      {/* Progress Timeline */}
      {showDetails && (
        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Application Progress</h3>
          {applicationStages?.map((stage, index) => (
            <div key={stage?.id} className="flex items-start space-x-4">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    stage?.completed
                      ? 'bg-success/10 border-success text-success' :'bg-muted/10 border-muted text-muted-foreground'
                  }`}
                >
                  {stage?.completed ? (
                    <Icon name="Check" size={20} />
                  ) : (
                    <span className="text-sm font-semibold">{stage?.id}</span>
                  )}
                </div>
                {index < applicationStages?.length - 1 && (
                  <div
                    className={`w-0.5 h-12 ${
                      stage?.completed ? 'bg-success' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
              <div className="flex-1 pb-8">
                <h4 className="font-semibold text-foreground">{stage?.title}</h4>
                <p className="text-sm text-muted-foreground">{stage?.description}</p>
                {stage?.date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(stage.date)?.toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border/50">
        <Button
          variant="ghost"
          iconName={showDetails ? 'ChevronUp' : 'ChevronDown'}
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </Button>
        <div className="flex items-center space-x-2">
          <Button variant="outline" iconName="Download" size="sm">
            Download Report
          </Button>
          <Button variant="default" iconName="RefreshCw" size="sm" onClick={onRefresh}>
            Refresh Status
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatus;